import React, { useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";

export default function ChatInteractionCard({
  interaction,
  disabled,
  onSubmit,
  className,
}) {
  const options = useMemo(
    () => (Array.isArray(interaction?.options) ? interaction.options : []),
    [interaction?.options]
  );
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const uploadInputRef = useRef(null);
  const [uploadOption, setUploadOption] = useState(null);

  const isMultiSelect = interaction?.type === "multi_select";
  const isChoiceButtons = interaction?.type === "choice_buttons";

  if ((!isMultiSelect && !isChoiceButtons) || options.length === 0) {
    return null;
  }

  const minSelected = Math.max(1, Number(interaction.minSelected || 1));
  const selectedOptions = options.filter((option) => selectedIds.has(option.id));
  const canSubmit = !disabled && selectedOptions.length >= minSelected;

  const toggleOption = (optionId) => {
    if (disabled) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(optionId)) {
        next.delete(optionId);
      } else {
        next.add(optionId);
      }
      return next;
    });
  };

  if (isChoiceButtons) {
    return (
      <div
        className={cn(
          "flex flex-wrap gap-2 rounded-md border border-border bg-background/85 p-3 text-foreground shadow-sm",
          disabled && "opacity-70",
          className
        )}
        role="group"
        aria-label="Asset source selection"
      >
        <input
          ref={uploadInputRef}
          type="file"
          className="hidden"
          multiple
          accept="image/*,video/*"
          onChange={(event) => {
            const files = Array.from(event.target.files || []);
            if (files.length && uploadOption) {
              onSubmit?.(interaction, [uploadOption], files);
            }
            event.target.value = "";
            setUploadOption(null);
          }}
        />
        {options.map((option) => (
          <Button
            key={option.id}
            type="button"
            variant={option.id === "upload_files" ? "default" : "outline"}
            size="sm"
            className="h-8"
            disabled={disabled}
            onClick={() => {
              if (option.id === "upload_files") {
                setUploadOption(option);
                uploadInputRef.current?.click();
                return;
              }
              onSubmit?.(interaction, [option]);
            }}
          >
            {option.label}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-md border border-border bg-background/85 p-3 text-foreground shadow-sm",
        disabled && "opacity-70",
        className
      )}
      role="group"
      aria-label="Channel selection"
    >
      <div className="grid gap-2">
        {options.map((option) => {
          const checked = selectedIds.has(option.id);
          return (
            <label
              key={option.id}
              className={cn(
                "flex min-h-10 cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm transition-colors",
                checked
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border bg-background hover:bg-accent/60",
                disabled && "cursor-not-allowed hover:bg-background"
              )}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={checked}
                disabled={disabled}
                onChange={() => toggleOption(option.id)}
              />
              <span
                className={cn(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded border",
                  checked ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background"
                )}
                aria-hidden="true"
              >
                {checked ? <Check className="h-3.5 w-3.5" strokeWidth={2.25} /> : null}
              </span>
              <span className="min-w-0 break-words">{option.label}</span>
            </label>
          );
        })}
      </div>
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          className="h-8"
          disabled={!canSubmit}
          onClick={() => onSubmit?.(interaction, selectedOptions)}
        >
          {interaction.submitLabel || "Continue"}
        </Button>
      </div>
    </div>
  );
}
